import { useState, useCallback } from 'react';
import styled from 'foundations-theming-specialty/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { useThemeBackground } from '../hooks/useThemeBackground';
import { usePortalNavigation } from '../hooks/usePortalNavigation';
import { useSideNavExpanded } from '../hooks/useSideNavExpanded';
import { useIsUngated } from '../hooks/useIsUngated';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { PortalsSideNav } from './PortalsNav';
import { MyAccountPopover } from './MyAccountPopover';
import { PortalsTopBar } from './PortalsTopBar';
import { PortalsNavDrawer } from './PortalsNavDrawer';
import { GlobalPortalStyles } from './GlobalPortalStyles';
import { PortalLogo } from './PortalLogo';
import { MAIN_CONTENT_ID, SkipToMainContent } from './SkipToMainContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SIDE_NAV_EXPANDED_WIDTH = '210px';
const SIDE_NAV_COLLAPSED_WIDTH = '56px';

// Bound into `@PortalFeatureGates` in PortalsPlatform and surfaced via
// loginVerify's `featuresEnabled` (see docs/gating.md). When the gate is off,
// the rail renders as the original fixed icon-only rail (no expand/collapse
// toggle).
export const EXPANDABLE_SIDE_NAV_GATE = 'PortalsPlatform::UnifiedShell::ExpandableSideNav';

// Trellis `View`/`Flex` intentionally don't expose `transition` as a style
// prop (their types include cursor but not TransitionStyleProps), so the
// animated width rail is a native styled element — same approach as the
// existing `HsRouteKeyTape`/`NavChevron` in the nav.
const SideNavContainer = styled.div.withConfig({
  displayName: "PortalsAppLayout__SideNavContainer"
})(({
  theme,
  $expanded
}) => ({
  boxSizing: 'border-box',
  flexShrink: 0,
  inlineSize: $expanded ? SIDE_NAV_EXPANDED_WIDTH : SIDE_NAV_COLLAPSED_WIDTH,
  paddingBlock: theme.space['400'],
  // Animate the rail width when toggling between collapsed and expanded.
  transition: 'inline-size 200ms ease, width 200ms ease',
  // Clip inline labels while the rail animates so they don't spill into the
  // main content area mid-transition. Hover popovers render in a portal and
  // are unaffected.
  overflow: 'hidden'
}));
function PortalsHeader({
  logoAccessory,
  shouldShowSideNav
}) {
  const borderProps = !shouldShowSideNav ? {
    borderBlockEndWidth: '100',
    borderBlockEndColor: 'fill-surface-raised',
    borderBlockEndStyle: 'solid'
  } : {};
  return /*#__PURE__*/_jsxs(Flex, Object.assign({
    alignItems: "center",
    blockSize: "56px",
    flexShrink: "0",
    paddingInlineStart: shouldShowSideNav ? '200' : '800',
    paddingInlineEnd: shouldShowSideNav ? '400' : '800'
  }, borderProps, {
    children: [/*#__PURE__*/_jsx(Flex, {
      flexShrink: 0,
      alignItems: "center",
      children: /*#__PURE__*/_jsx(PortalLogo, {
        height: "40px",
        maxWidth: "150px",
        fallbackMaxInlineSize: "300px"
      })
    }), logoAccessory ? /*#__PURE__*/_jsx(Flex, {
      flexShrink: 0,
      alignItems: "center",
      children: logoAccessory
    }) : null, /*#__PURE__*/_jsx(Flex, {
      flexGrow: "1"
    }), /*#__PURE__*/_jsx(Flex, {
      flexShrink: 0,
      alignItems: "center",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(MyAccountPopover, {})
    })]
  }));
}
export function PortalsAppLayout({
  children,
  logoAccessory: logoAccessoryProp,
  headerStart,
  history,
  currentPathname,
  hideSideNav = false
}) {
  const logoAccessory = logoAccessoryProp !== null && logoAccessoryProp !== void 0 ? logoAccessoryProp : headerStart;
  const isPill = useIsPillBorderRadius();
  const {
    shouldShowSideNav
  } = usePortalNavigation(currentPathname);
  const {
    shell,
    mainContentArea
  } = useThemeBackground();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isExpandableSideNavEnabled = useIsUngated(EXPANDABLE_SIDE_NAV_GATE);
  const [isSideNavExpanded, toggleSideNavExpanded] = useSideNavExpanded();
  // When the gate is off, force the collapsed icon rail and hide the toggle.
  const sideNavExpanded = isExpandableSideNavEnabled && isSideNavExpanded;
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const navBackgroundColor = shouldShowSideNav ? shell : mainContentArea;
  const shouldShowDesktopSideNav = !isMobile && shouldShowSideNav && !hideSideNav;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    blockSize: "100vh",
    overflow: "hidden",
    backgroundColor: navBackgroundColor,
    children: [/*#__PURE__*/_jsx(GlobalPortalStyles, {}), /*#__PURE__*/_jsx(SkipToMainContent, {}), isMobile ? /*#__PURE__*/_jsx(PortalsTopBar, {
      onHamburgerClick: openDrawer,
      isDrawerOpen: isDrawerOpen,
      shouldShowSideNav: shouldShowSideNav,
      logoAccessory: logoAccessory,
      navBackgroundColor: navBackgroundColor
    }) : /*#__PURE__*/_jsx(PortalsHeader, {
      logoAccessory: logoAccessory,
      shouldShowSideNav: shouldShowSideNav
    }), /*#__PURE__*/_jsxs(Flex, {
      flexGrow: "1",
      overflow: "hidden",
      minBlockSize: "0px",
      children: [shouldShowDesktopSideNav && /*#__PURE__*/_jsx(SideNavContainer, {
        $expanded: sideNavExpanded,
        children: /*#__PURE__*/_jsx(PortalsSideNav, {
          history: history,
          currentPathname: currentPathname,
          isExpanded: sideNavExpanded,
          expandedInlineSize: SIDE_NAV_EXPANDED_WIDTH,
          onToggleExpanded: isExpandableSideNavEnabled ? toggleSideNavExpanded : undefined
        })
      }), /*#__PURE__*/_jsx(View, {
        as: "main",
        id: MAIN_CONTENT_ID,
        tabIndex: -1,
        flexGrow: "1",
        backgroundColor: mainContentArea,
        minInlineSize: "0px",
        borderStartStartRadius: shouldShowDesktopSideNav ? isPill ? '0' : '400' : undefined,
        overflow: "auto",
        children: children
      }, "portal-main")]
    }), isMobile && shouldShowSideNav ? /*#__PURE__*/_jsx(PortalsNavDrawer, {
      open: isDrawerOpen,
      onClose: closeDrawer,
      history: history,
      currentPathname: currentPathname
    }) : null]
  });
}